const ActionType = {
  UPDATE: 'UPDATE',
  DUPLICATE: 'DUPLICATE',
  ARCHIVE: 'ARCHIVE',
  UNARCHIVE: 'UNARCHIVE',
  CREATE: 'CREATE',
};

const canPerformAction = (actionType, program) => (
  !!actionType && !!program
);

const isEditAction = (actionType, program) => (
  canPerformAction(actionType, program) && actionType === ActionType.UPDATE
);

const isCreateAction = (actionType) => (
  actionType === ActionType.CREATE
);

const isEditorView = (actionType, program) => (
  isEditAction(actionType, program) || isCreateAction(actionType)
);

/**
 * Count of a workout sharing across a coach's programs. Programs reference workouts so the same workout doc can be
 * shared by multiple programs and/or by multiple days within a single program.
 *
 * @param {Object} params
 * @param {Array<{ workouts: Array<{ workoutRef: ?string }> }>} [params.otherPrograms] -
 *   The coach's programs EXCLUDING the one currently being edited.
 * @param {Array<{ id: ?string }>} [params.currentProgramDays] - The current program's days. Rest days have a null `id`.
 * @param {?string} params.workoutId - The workout being edited.
 * @param {number} params.currentDayIndex - Index of the day being edited
 * @returns {{ otherProgramCount: number, sameProgramOtherDayCount: number }}
 */
const getWorkoutUsageCounts = ({
  otherPrograms = [],
  currentProgramDays = [],
  workoutId,
  currentDayIndex,
} = {}) => {
  if (!workoutId) {
    return { otherProgramCount: 0, sameProgramOtherDayCount: 0 };
  }

  const otherProgramCount = otherPrograms.filter(
    (prog) => (prog.workouts || []).some((w) => w && w.workoutRef === workoutId),
  ).length;

  const sameProgramOtherDayCount = currentProgramDays.filter(
    (workoutDay, index) => index !== currentDayIndex && workoutDay && workoutDay.id === workoutId,
  ).length;

  return { otherProgramCount, sameProgramOtherDayCount };
};

/**
 * Builds a Formik workout-day value from a workout document.
 *
 * @param {Object} workoutDetails - A Workout document.
 * @returns {{ id: string, label: string, equipment: Array, workout: Object }}
 */
const formatWorkoutDay = (workoutDetails) => ({
  id: workoutDetails.id,
  label: workoutDetails.name,
  equipment: workoutDetails.equipmentList,
  workout: workoutDetails,
});

/**
 * Serializes the editor's workout-day form values into the persisted program `workouts` shape
 * (`[{ workoutRef }]`). Rest days (no `id`) persist as `{ workoutRef: null }`.
 *
 * @param {Array<{ id: ?string }>} days
 * @returns {Array<{ workoutRef: ?string }>}
 */
const toWorkoutRefs = (days) => days.map((day) => ({ workoutRef: day.id || null }));

/**
 * Whether a program is public and NOT owned by the current coach.
 *
 * @param {Object} programDoc
 * @param {Object} coachDoc
 * @returns {boolean}
 */
const isUnownedPublicProgram = (programDoc, coachDoc) => (
  !!programDoc?.isPublic && !coachDoc?.isPublicTracksCoach
);

export {
  ActionType,
  isEditAction,
  isEditorView,
  getWorkoutUsageCounts,
  formatWorkoutDay,
  toWorkoutRefs,
  isUnownedPublicProgram,
};
